import { AfterViewInit, Component, ElementRef, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { BaseWorkItemService } from "../services/base-work-item-service";
import { TableConfiguration } from "../models/table-configuration.model";
import { BehaviorSubject, Subscription } from "rxjs";
import { RequestWorkItem } from "../models/request.model";
import { CollectionWorkItem } from "../models/collection.model";
import { UploadWorkItem } from "../models/upload.model";
import { VirtualEnvironmentWorkItem } from "../models/virtualenv.model";
import { MatSort, SortDirection } from "@angular/material/sort";
import { debounceTime, distinctUntilChanged, finalize, skip, tap } from "rxjs/operators";
import { WorkItem, WorkItemType } from "../models/work-item.model";
import { DragScrollComponent } from "ngx-drag-scroll";
import { animate, state, style, transition, trigger } from "@angular/animations";
import { EntityType } from "../models/entity-type";

@Component({
  selector: 'app-work-item-table',
  templateUrl: './work-item-table.component.html',
  styleUrls: ['./work-item-table.component.scss'],
  // DUR custom row animation
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class WorkItemTableComponent<
  TModel extends (CollectionWorkItem | UploadWorkItem | RequestWorkItem | VirtualEnvironmentWorkItem | WorkItem),
  TService extends BaseWorkItemService<TModel> = BaseWorkItemService<TModel>
> implements OnInit, AfterViewInit {
  // inputs
  @Input()
  workItemService: TService;
  @Input()
  public tableConfiguration: TableConfiguration;
  @Input()
  entityType: EntityType = null;

  // defaults
  public get defaultSortField(): string {
    return 'lastModifiedTime';
  }

  public get defaultSortDirection(): SortDirection {
    return 'desc';
  }

  private readonly defaultColumns: string[] = [
    'creationTime', // DC, DUR, DU, VER
    'lastModifiedTime', // Home, DC, DUR, DU, VER
    'type', // Home
    'name', // Home, DC, DUR, DU, VER
    'status', // Home, DC, DUR, DU, VER
    'custodianName', // DC
    'requestorName', // DUR, VER
    'creatorName', // DU
    'collectionName', // DUR, DU
    'actions' // DUR
  ];

  private readonly defaultColumnTitles: object = {
    creationTime: 'Created',
    lastModifiedTime: 'Modified',
    type: 'Type',
    name: 'Name',
    status: 'Status',
    custodianName: 'Custodian',
    requestorName: 'Requestor',
    creatorName: 'Creator',
    collectionName: 'Collection'
  };

  // props
  public get columnsToDisplay(): string[] {
    if (!this.tableConfiguration?.columns)
      return this.defaultColumns;

    const columns = this.defaultColumns.filter(c => this.tableConfiguration.columns[c] !== undefined);

    // add action for DUR
    if (this.entityType === EntityType.Request && !columns.some(c => c === 'actions'))
      columns.push('actions');

    return columns;
  }

  public dataSource: TModel[] = [];
  private sortBy: string;
  private isDesc: boolean;
  private searchText: string;
  private selectedFilters: string[];
  public continuationToken: string;

  public loading: boolean = false;

  @ViewChild(MatSort, { static: false }) sort: MatSort;

  private searchTextChanged$ = new BehaviorSubject<string>("");
  private filtersChanged$ = new BehaviorSubject<string[]>([]);
  private subscriptions: Subscription[] = [];

  // DUR custom row
  public expandedElement: TModel | null;
  public rightScrollVisibility: Array<boolean> = [];
  @ViewChildren(DragScrollComponent) dragScrolls: QueryList<DragScrollComponent>;

  // ctor and dispose
  constructor(private host: ElementRef) {
    this.sortBy = this.defaultSortField;
    this.isDesc = this.defaultSortDirection === 'desc';
    this.selectedFilters = [];
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  // methods
  ngOnInit(): void {
    this.loadWorkItems();
  }

  ngAfterViewInit() {
    // sort or page change
    this.subscriptions.push(this.sort.sortChange.pipe(
      tap(() => {
        this.dataSource = [];
        this.continuationToken = null;
        this.sortBy = this.sort.active;
        this.isDesc = this.sort.direction === 'desc';
        this.loadWorkItems();
      })
    ).subscribe());

    // search change
    this.subscriptions.push(this.searchTextChanged$.pipe(
      skip(1),
      debounceTime(500),
      distinctUntilChanged(),
      tap(searchText => {
        this.dataSource = [];
        this.continuationToken = null;
        this.searchText = searchText;
        this.loadWorkItems();
      })
    ).subscribe());

    // filters change
    this.subscriptions.push(this.filtersChanged$.pipe(
      skip(1),
      tap(() => {
        this.dataSource = [];
        this.continuationToken = null;
        this.loadWorkItems();
      })
    ).subscribe());
  }

  loadWorkItems() {
    this.loading = true;
    this.initDrags();

    this.workItemService.getPage(this.sortBy, this.isDesc, this.searchText, this.selectedFilters, this.continuationToken).pipe(
      finalize(() => this.loading = false)
    ).subscribe(data => {
      this.initDrags();

      this.continuationToken = data.continuationToken;
      this.dataSource = this.dataSource.concat(data.items);

      // scroll to the top of the page
      const elements = this.host.nativeElement.getElementsByClassName('dl-absolute-fill-in-overflow');
      if (elements?.length > 0)
        elements[0].scrollTop = elements[0].scrollHeight;
    });
  }

  public getColumnTitle(columnDefinition: string): string {
    return this.tableConfiguration?.columns[columnDefinition] ?? this.defaultColumnTitles[columnDefinition] ?? '';
  }

  public getEntityType(element: any): EntityType {
    if (this.entityType)
      return this.entityType;

    switch (element.type) {
      case WorkItemType.Collection:
        return EntityType.Collection;
      case WorkItemType.Request:
        return EntityType.Request;
      case WorkItemType.Upload:
        return EntityType.Upload;
      case WorkItemType.VirtualEnvironment:
        return EntityType.VirtualEnvironment;
    }

    return null;
  }

  public getLink(element: any): string {
    const entityType = this.getEntityType(element);

    switch (entityType) {
      case EntityType.Collection:
        return `/data-collections/${element.entityId}/${element.creatorId}`;
      case EntityType.Request:
        return `/data-utilization/${element.entityId}/${element.creatorId}/${element.version}`;
      case EntityType.Upload:
        return `/data-uploads/${element.entityId}/${element.collectionId}`;
      case EntityType.VirtualEnvironment:
        return `/virtual-env/${element.entityId}/${element.creatorId}`;
    }

    return null;
  }

  // filter methods
  onFilterClick(filter: string) {
    this.toggleFilters(filter);
    this.filtersChanged$.next(this.selectedFilters);
  }

  isFilterActivated(filter: string): boolean {
    const filterIndex = this.getActivatedFilterIndex(filter);
    return filterIndex > -1;
  }

  private toggleFilters(filter: string) {
    const filterIndex = this.getActivatedFilterIndex(filter);

    if (filterIndex < 0)
      this.selectedFilters.push(filter);
    else
      this.selectedFilters.splice(filterIndex, 1);
  }

  private getActivatedFilterIndex(filter: string): number {
    return this.selectedFilters.indexOf(filter);
  }

  // search methods
  keyUp(event: KeyboardEvent) {
    this.searchTextChanged$.next((event.target as HTMLInputElement).value.trim().toLowerCase());
  }


  // DUR custom row drag
  private initDrags(): void {
    setTimeout(() => {
      this.dragScrolls.toArray().forEach(drag => {
        drag.moveTo(1000);
        this.rightScrollVisibility.push(false);
      });
    }, 100);
  }

  scrollLeft(drag: DragScrollComponent) {
    drag.moveLeft();
    this.rightScrollVisibility[this.getVisibilityIndex(drag)] = true;
  }

  scrollRight(drag: DragScrollComponent) {
    const prevIndex = drag.currIndex;
    drag.moveRight();
    if (prevIndex < drag.currIndex) {
      this.checkIfRightScrollAvailable(drag);
    }
  }

  checkIfRightScrollAvailable(drag: DragScrollComponent) {
    const prevIndex = drag.currIndex;
    drag.moveRight();
    if (prevIndex < drag.currIndex) {
      drag.moveLeft();
      this.rightScrollVisibility[this.getVisibilityIndex(drag)] = true;
    } else {
      this.rightScrollVisibility[this.getVisibilityIndex(drag)] = false;
    }
  }

  getVisibilityIndex(drag: DragScrollComponent): number {
    return this.dragScrolls.toArray().indexOf(drag);
  }

  openDetails(event: MouseEvent, element: TModel) {
    event.stopPropagation();
    this.expandedElement = this.expandedElement === element ? null : element;
  }
}
